import { useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BaseDialog } from 'src/components/BaseDialog/BaseDialog';
import { ts } from 'src/utils/translate';
import { theme } from 'src/theme/mui-theme';
import { LoxoneIcon } from 'src/icons/LoxoneIcon';
import { ConventionalIcon } from 'src/icons/ConventionalIcon';

export enum AutoPlanBundle {
    SMARTHOME = 'smarthome',
    CONVENTIONAL = 'conventional',
}

interface StartAutoPlanModalProps {
    onSelect: (bundle: AutoPlanBundle) => void;
    onClose: () => void;
}

interface BundleOption {
    id: AutoPlanBundle;
    label: string;
    description: string;
    Icon: typeof LoxoneIcon;
}

export function StartAutoPlanModal({ onSelect, onClose }: StartAutoPlanModalProps) {
    const [selected, setSelected] = useState<AutoPlanBundle | null>(null);

    const options: BundleOption[] = [
        {
            id: AutoPlanBundle.SMARTHOME,
            label: ts('start_auto_plan_loxone', 'Loxone'),
            description: ts('start_auto_plan_loxone_desc', 'Your project will include Loxone system functions'),
            Icon: LoxoneIcon,
        },
        {
            id: AutoPlanBundle.CONVENTIONAL,
            label: ts('start_auto_plan_conventional', 'Conventional'),
            description: ts('start_auto_plan_conventional_desc', 'Your project will include Conventional system functions'),
            Icon: ConventionalIcon,
        },
    ];

    return (
        <BaseDialog
            onClose={onClose}
            hideClose
            disableClose
            title={ts('start_auto_plan_title', 'Start Auto-plan')}
            subtitle={ts('start_auto_plan_desc', 'All devices will be auto-placed according to DIN and Loxone standards.')}
            titleColor={theme.palette.primary.main}
            actionLabel={ts('continue_button_label', 'Continue')}
            actionDisabled={selected == null}
            onAction={() => { if (selected != null) onSelect(selected); }}
        >
            <Stack alignItems="center" spacing={2.5} sx={{ pt: 1, pb: 1 }}>
                <Typography>
                    {ts('start_auto_plan_select', 'Select plan type below:')}
                </Typography>
                <Stack direction="row" spacing={3} sx={{ width: '100%' }}>
                    {options.map(({ id, label, description, Icon }) => {
                        const isSelected = selected === id;
                        return (
                            <Stack
                                key={id}
                                alignItems="center"
                                onClick={() => setSelected(id)}
                                sx={{
                                    flex: 1,
                                    cursor: 'pointer',
                                    borderRadius: 2,
                                    border: `2px solid ${isSelected ? theme.palette.primary.main : theme.palette.grey[300]}`,
                                    backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                                    p: 2,
                                }}
                            >
                                <Icon sx={{ fontSize: 60, color: theme.palette.primary.main }} />
                                <Typography
                                    variant="subtitle2"
                                    color={theme.palette.primary.main}
                                    textAlign="center"
                                    sx={{ mt: 1.5 }}
                                >
                                    {label}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color={theme.palette.text.secondary}
                                    textAlign="center"
                                    sx={{ mt: 0.5 }}
                                >
                                    {description}
                                </Typography>
                            </Stack>
                        );
                    })}
                </Stack>
            </Stack>
        </BaseDialog>
    );
}
